import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { useRouter } from "next/router";
import { useAtom, useAtomValue } from "jotai";
import { Column } from "@/models/table";
import { isEmpty } from "lodash";
import { backgroundColorState, textColorState, themeState, userState } from "@/lib/store";
import { collectMessages, emptyLiveState, LiveMessage, LiveState, planSends, subscriptionKey } from "./liveSubscriptions";
import {
    DataGridPremium,
    GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
    GRID_DETAIL_PANEL_TOGGLE_FIELD,
    GridFilterModel,
    GridPinnedRowsProp,
    GridRenderCellParams,
    GridRow,
    GridSortDirection,
    GridSortModel,
    gridDetailPanelExpandedRowsContentCacheSelector,
    useGridApiContext,
    useGridApiRef,
    useGridSelector,
} from "@mui/x-data-grid-premium";
import { IconButton } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import TableCell from "@/components/table-cell/TableCell";
import { TableCellContext } from "@/components/table-cell/cellContext";
import HeaderFilters from "./HeaderFilters";

// Live rows prepended through extraRow (the options flow streams one per trade) are capped so a
// grid left open all day does not keep every trade; the oldest live rows drop off first.
const MAX_EXTRA_ROWS = 500;

/**
 * Which column the company picker filters on, in priority order.
 *
 * It used to be "whichever of these the payload happens to list first", and a
 * payload that carries a row `id` lists it first - so on the insider filings
 * grid the picker sent the chosen instrument's id under the `id` column, the
 * grid's own filing id, and nothing filtered. Both spellings are here because
 * responses are camelCase and this list was written in snake_case.
 */
const INSTRUMENT_FILTER_FIELDS = ["stock_id", "stockId", "tradingitem_id", "tradingitemId", "security_id", "securityId", "company_id", "companyId", "id"];

const instrumentColumns = (headers: Array<{ field: string }>) => {
    for (const field of INSTRUMENT_FILTER_FIELDS) {
        const matches = headers.filter((header) => header.field === field);
        if (matches.length) return matches;
    }
    return [];
};
import { ContentLayoutContext, Context } from "@/lib/Context";
import { DataGridHeaders, FilterState, DataGridProps } from "@/models/datagrid";
import { areEqual, isOrtexApp } from "@/lib/utils";
import { useMedia } from "react-use";
import useOpenSubscriptionForm from "src/hooks/useOpenSubscriptionForm";
import { UPGRADE_LOCATIONS, trackUpgradeClick } from "@/lib/tracking";
import { autosizeOptions, BASE_WIDTH, formatCell, getExtraHiddenCols, getFilterableCols } from "./dataGrid.utils";
import { cellHoverStyleOptions, DataGridStyles, tableStyleOptions, useCellStyleOptions } from "./DataGridStyles";
import { useTouchAxisLock } from "./hooks/useTouchAxisLock";
import { PREMIUM_FEATURES_OFF } from "./premium";
import { gridFetchParams, initialRowsQty as rowsQtyFor, savedGridFilters } from "./requestParams";
import { isColumnCleanupOnly } from "./sortModel";
import { styled, Tooltip, tooltipClasses, TooltipProps } from "@mui/material";

interface HtmlTooltipProps extends TooltipProps {
    bgColor?: string;
    fgColor?: string;
}

/** The shortest strip of scrambled rows the unlock banner is drawn over (its small layout fits in it). */
const LOCKED_BANNER_MIN_HEIGHT = 120;

const HtmlTooltip = styled(({ className, bgColor: _b, fgColor: _f, ...props }: HtmlTooltipProps) => <Tooltip {...props} classes={{ popper: className }} />)<HtmlTooltipProps>(({ bgColor, fgColor }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: bgColor,
        color: fgColor,
        maxWidth: 420,
        fontSize: "0.8rem",
        padding: "10px 14px",
        lineHeight: 1.5,
    },
    [`& .${tooltipClasses.arrow}`]: {
        color: bgColor,
    },
}));
import { NoRowsComponent } from "./NoRows";
import ObfuscationBanner from "./ObfuscationBanner";
import { firstScrambledRowId, freeRowIds, GridObfuscation, isCellScrambled, lockedRegionTop, readObfuscation, RenderedRow } from "./freeRows";
import SnackBar from "../snack-bar/SnackBar";
import { updateUserSettings } from "@/controllers/auth";

let rowsHeightUpdateTimeout: NodeJS.Timeout | null = null;

/**
 * The detail panel's toggle (`detailPanel` prop): a chevron on the rows that have a panel, nothing
 * on the rows that do not (MUI's own toggle shows a disabled chevron on every row). Its click
 * opens the panel only, never the row.
 */
const DetailPanelToggle = ({ id, value: expanded, label }: GridRenderCellParams & { label: string }) => {
    const apiRef = useGridApiContext();
    // useGridSelector is typed for the community grid API, and this selector reads Pro state, so the
    // selector needs a cast even with @types/react 19 (a GridApiPremium apiRef does not help).
    const contentCache = useGridSelector(apiRef, gridDetailPanelExpandedRowsContentCacheSelector as any) as Record<string, React.ReactNode>;
    if (!React.isValidElement(contentCache[id])) return null;
    return (
        <IconButton
            size="small"
            aria-label={expanded ? `Hide ${label}` : `Show ${label}`}
            aria-expanded={!!expanded}
            onClick={(event) => {
                event.stopPropagation();
                apiRef.current.toggleDetailPanel(id);
            }}
        >
            <KeyboardArrowDownIcon fontSize="small" style={{ transform: expanded ? "rotate(180deg)" : undefined, transition: "transform 150ms" }} />
        </IconButton>
    );
};

const sanitizeFilterItems = (model: GridFilterModel): GridFilterModel => ({
    ...model,
    items: model.items
        .filter((item: any) => item.field || item.columnField)
        .map(({ operatorValue, operator, columnField, ...item }: any) => ({
            ...item,
            columnField: columnField ?? item.field,
            operatorValue: operatorValue ?? operator,
            field: item.field ?? columnField,
            operator: operator ?? operatorValue ?? "contains",
        })),
});

export const DataGrid = ({
    applyFiltersToChart,
    autoHeight = false,
    extraPinnedTopRows,
    button,
    customRowHeight = "auto",
    data,
    datetime,
    expand,
    expandId,
    expandableRows,
    extraButton,
    extraRow,
    fetcher,
    getRowClassName,
    setFetcherResults,
    filterColumnType,
    id,
    isNewsSearch,
    liveData,
    onePage,
    regionId,
    reset,
    rowsAmount = 35,
    setExportingParams,
    setFilters,
    specifiedColumns,
    stock,
    last_close,
    tableId,
    maxFilterSets,
    updateAvailableColumns,
    updateCurrentColumns,
    rowTooltipField,
    noPrefixDate,
    noRowsMessage,
    noCalendarPresets,
    forwardLookingDateFields,
    wrapStockCell,
    detailPanel,
    cellRenderers,
    ...props
}: DataGridProps) => {
    const { openSubscriptionForm } = useOpenSubscriptionForm();
    // One subscription-form handler for every cell (TableCellContext), stable across renders.
    // A visitor registers instead: the subscription form has no account to attach to.
    // Read through refs: the router's `push` changes identity with route state, and the cell
    // context must not.
    const { push } = useRouter();
    const pushRef = useRef(push);
    pushRef.current = push;
    const isVisitorRef = useRef(false);
    const openSubscriptionFormRef = useRef(openSubscriptionForm);
    openSubscriptionFormRef.current = openSubscriptionForm;
    const openUpgrade = useCallback(() => (isVisitorRef.current ? pushRef.current("/login?register=true") : openSubscriptionFormRef.current()), []);
    const cellShared = useMemo(() => ({ openSubscriptionForm: openUpgrade }), [openUpgrade]);
    const { setDisableFilters } = useContext(ContentLayoutContext);
    const { ortexWebSocket } = useContext(Context);

    const theme = useAtomValue(themeState);
    const backgroundColor = useAtomValue(backgroundColorState);
    const textColor = useAtomValue(textColorState);
    const [user, setUser] = useAtom(userState);
    isVisitorRef.current = !user?.subscription_plan || user.subscription_plan === "ORTEX Visitor";

    const [apiParams, setApiParams] = useState({});
    const [headers, setHeaders] = useState([]);
    // What the headers were last built from besides the columns: rebuilt only when it changes (see applyResponse).
    const headersSource = useRef<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [customFilters, setCustomFilters] = useState([]);
    const [customFilterModel, setCustomFiltersModel] = useState<GridFilterModel>({ items: [] });
    const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
    const [sortModel, setSortModel] = useState<GridSortModel>([]);
    const [rows, setRows] = useState([]);
    const [noFilters, setNoFilters] = useState<boolean>(true);
    const [hasMoreRows, setHasMoreRows] = useState<boolean>(true);
    const windowWidth = useRef(typeof window !== "undefined" && window.innerWidth);
    const windowHeight = useRef(typeof window !== "undefined" && window.innerHeight);
    const [filtersCleared, setFiltersCleared] = useState<boolean>(false);
    const [filtersState, setFiltersState] = useState<FilterState>({ previousState: "initial", currentState: "initial" });

    const [clickeableCells, setClickeableCells] = useState(null);
    const [paginationLinks, setPaginationLinks] = useState<any>(false);
    const [resetFilters, setResetFilters] = useState<any>(false);
    const [pinnedCols, setPinnedCols] = useState<{ left: string[]; right: string[] }>({ left: [], right: [] });

    const [pinnedRows, setPinnedRows] = useState<GridPinnedRowsProp>({ top: [], bottom: [] });
    const [chips, setChips] = useState([]);
    const [initialParam, setInitialParam] = useState(false);
    const [snackBar, setSnackBar] = useState<{ open: boolean; text: string; severity: "success" | "error" | "info" | "warning" }>({ open: false, text: "", severity: "success" });

    const obfuscatedRef = useRef(false);
    // The answer's obfuscation block and the rows it returned unscrambled (./freeRows). Only the cells'
    // blur reads them: the grid's lock (sort, filters, columns, next page) stays on obfuscatedRef.
    const obfuscationRef = useRef<GridObfuscation>({ obfuscated: false, columns: null, freeRows: 0 });
    const freeRowsRef = useRef<Set<string | number>>(new Set());

    const defaultValues = useRef(null);
    const controller = useRef<AbortController>(undefined);
    const page = useRef<number>(1);
    const tableRef = useGridApiRef();
    useTouchAxisLock(tableRef);
    const isMounted = useRef<boolean>(false);
    const totalPages = useRef<number>(1);
    const hiddenColumns = useRef<Record<string, any>>({});
    const columnSaveTimeout = useRef<NodeJS.Timeout | null>(null);
    const activeColumnOrder = useRef<string[] | null>(null);
    const isColumnChangeFetch = useRef<boolean>(false);

    const saveColumnOrderRef = useRef<(fields: string[]) => void>(null);
    saveColumnOrderRef.current = (orderedFields: string[]) => {
        if (!tableId || user?.subscription_plan === "ORTEX Visitor") return;
        const newSavedDGFilters = { ...user.settings.module_settings?.["datagrid->saved-filters"] };
        newSavedDGFilters[tableId] = { ...newSavedDGFilters[tableId], columnConfiguration: orderedFields };
        const settings = { ...user.settings, module_settings: { ...user.settings?.module_settings, ["datagrid->saved-filters"]: newSavedDGFilters } };
        if (!process.env.NEXT_PUBLIC_VERCEL_ENV) {
            console.log("[DEV] updateUserSettings payload:", settings);
            return;
        }
        setUser({ ...user, settings });
        try {
            updateUserSettings(settings);
            setSnackBar({ open: true, text: "Column order saved", severity: "success" });
        } catch {
            setSnackBar({ open: true, text: "Error saving column order", severity: "error" });
        }
    };
    const currentColumns = useRef<string[]>([]);

    const isMobile = useMedia("(max-width: 768px)");
    const isLaptop = useMedia("(max-width: 1280px)");

    const initialRowsQty = rowsQtyFor(windowWidth.current, windowHeight.current, rowsAmount);

    function getMinWidth(flex: number, format?: string, pinDirection?: string) {
        const isButtonColumn = format === "button" || format === "extra-button";

        if (isMobile || isOrtexApp()) {
            // A pinned stock shows its ticker only on a phone (TableCell), leaving the screen to the data.
            if (pinDirection && (format === "company" || format === "company_w_logo")) return 72;
            return isButtonColumn ? 56 : 120;
        }

        if (flex === undefined) return 120;

        return Math.max(flex * BASE_WIDTH, 120);
    }

    const extraRowIds = useRef<unknown[]>([]);
    const rowSubscriptionData = useRef<{ [ticker: string]: { price: number | null; unsubscribed: boolean } }>({});

    const updateRowSubscriptionData = useCallback((ticker, data) => {
        rowSubscriptionData.current[ticker] = {
            ...rowSubscriptionData.current[ticker],
            ...data,
        };
    }, []);

    const getRowSubscriptionData = useCallback((ticker) => {
        return rowSubscriptionData.current[ticker] || { price: null, unsubscribed: false };
    }, []);

    // Live prices: one message per instrument and kind on screen, per liveData entry. `liveKey` changes only when an
    // instrument enters or leaves the rows, so a price refresh or a page append no longer resends every row (#1285
    // item 10). The grid never unsubscribes (see `planSends`): `liveStateRef[i]` is what entry i has sent on its socket,
    // `liveNextRef[i]` what its rows need now.
    const liveStateRef = useRef<LiveState[]>([]);
    const liveNextRef = useRef<Map<string, LiveMessage>[]>([]);
    // Every caller passes a fixed-length liveData array, so spreading the sockets into the dependency lists is stable.
    const liveSockets = (liveData || []).map((obj) => obj.websocket || ortexWebSocket);
    const liveKey = subscriptionKey(rows, liveData);

    // Send what entry i's rows need and its socket has not had. A socket that is not open has had nothing, so only
    // the rows are recorded and the open handler below sends them once it opens.
    const flushLive = (ws: WebSocket, i: number) => {
        if (ws?.readyState !== WebSocket.OPEN) return;
        const { frames, state } = planSends(liveNextRef.current[i] || new Map<string, LiveMessage>(), liveStateRef.current[i] || emptyLiveState());
        frames.forEach((frame) => ws.send(JSON.stringify(frame)));
        liveStateRef.current[i] = state;
    };

    const syncLiveSubscriptions = (i: number) => {
        liveNextRef.current[i] = collectMessages(rows, liveData[i].message);
        flushLive(liveSockets[i], i);
    };

    useEffect(() => {
        if (!liveData) return;
        try {
            liveData.forEach((_, i) => syncLiveSubscriptions(i));
        } catch (error) {
            console.error("Error with WS ", error);
        }
    }, [liveKey, ...liveSockets]);

    // Per socket: one named open handler that sends the current set when the socket opens, removed on cleanup, and a
    // sync for a socket that is already open when bound. A reconnect replaces the socket (useAppWebSockets), so the
    // next socket starts from nothing sent.
    useEffect(() => {
        if (!liveData) return;
        const handlers = liveSockets.map((ws, i) => {
            if (!ws) return null;
            const handler = () => {
                try {
                    flushLive(ws, i);
                } catch (error) {
                    console.error("Error with WS ", error);
                }
            };
            ws.addEventListener("open", handler);
            return handler;
        });
        try {
            liveData.forEach((_, i) => syncLiveSubscriptions(i));
        } catch (error) {
            console.error("Error with WS ", error);
        }

        return () => {
            handlers.forEach((handler, i) => {
                const ws = liveSockets[i];
                if (ws && handler) ws.removeEventListener("open", handler);
                liveStateRef.current[i] = emptyLiveState();
            });
        };
    }, [...liveSockets]);

    useEffect(() => {
        if (!hiddenColumns.current?.mobile || !hiddenColumns.current?.laptop) return;
        const extraHiddenCols = getExtraHiddenCols(props.hiddenColumns);

        if (isMobile || isOrtexApp()) {
            setColumnVisibilityModel({ ...hiddenColumns.current.mobile, ...extraHiddenCols });
        } else if (isLaptop) {
            setColumnVisibilityModel({ ...hiddenColumns.current.laptop, ...extraHiddenCols });
        }
    }, [rows, isMobile, isLaptop]); // Don't change dependencies

    const pinnedList = useRef({ left: [], right: [] });

    const createColumn = (header: Column, obfuscated?: boolean) => {
        const { field, flex, hasTooltip, headerAlign, align, headerName, sortable, filterable, pinDirection, initialSort } = header;

        if (pinDirection && !pinnedList.current[pinDirection].includes(field)) pinnedList.current[pinDirection].push(field);
        if (filterable && noFilters) setNoFilters(false);

        let sortingOrder: GridSortDirection[] = ["asc", "desc"];
        if (initialSort === "desc") {
            sortingOrder = ["desc", "asc"];
        }

        const column: DataGridHeaders = {
            ...(pinDirection && { pinnable: false }),
            headerAlign: headerAlign || "left",
            ...(align && { align }),
            field,
            headerName,
            // The serializer's column description, shown as the header's tooltip.
            ...(header.description && { description: header.description }),
            sortable: obfuscated ? !obfuscated : sortable,
            filterable,
            flex: isMobile || isOrtexApp() ? null : flex || 1,
            minWidth: getMinWidth(flex, header.format, pinDirection),
            disableColumnMenu: !headerName,
            ...(initialSort && { sortingOrder }),
            renderCell: (params) => {
                // Blurred unless TED returned this row unscrambled (its free top rows; ./freeRows).
                const rowIsFree = obfuscatedRef.current && freeRowsRef.current.has(params.id);
                const obfuscatedRow = obfuscatedRef.current && isCellScrambled(obfuscationRef.current, rowIsFree, !!header.alwaysObfuscate);

                const custom = cellRenderers?.[field];
                if (custom) {
                    const drawn = custom(params.row);
                    if (drawn !== null && drawn !== undefined) return drawn;
                }

                return (
                    <TableCell
                        button={button}
                        extraButton={extraButton && (headerName === "Status" ? extraButton.filter((item) => item.label === "Reset Alert" || item.label === "Renew Alert") : extraButton.find((item) => item.label === headerName))}
                        datetime={datetime}
                        header={header}
                        liveData={liveData}
                        onRowClick={(!obfuscatedRef.current || rowIsFree) && props.onRowClick}
                        row={params.row}
                        value={hasTooltip ? [params.value[0]] : formatCell(params.value, header)}
                        tooltip={params.value?.[1]}
                        className={obfuscatedRow ? "obfuscated" : undefined}
                        last_close={last_close || false}
                        key={header?.field + params.row?.id}
                        updateRowSubscriptionData={updateRowSubscriptionData}
                        getRowSubscriptionData={getRowSubscriptionData}
                        rowNote={rowTooltipField ? !!params.row?.[rowTooltipField] : false}
                        noPrefixDate={noPrefixDate}
                        wrapStockCell={wrapStockCell}
                    />
                );
            },
            getApplyQuickFilterFn: null,
        };

        return column;
    };

    const handlePinnedColsChange = (newPinnedCols) => {
        pinnedList.current = newPinnedCols;
        setPinnedCols(newPinnedCols);
    };

    const formatHeaders = (headers, obfuscated = false, fieldOrder?: string[]) => {
        if (!Array.isArray(headers)) {
            console.error("headers is not an array:", headers);
            return [];
        }

        const result = [];
        if (fieldOrder?.length) {
            headers.sort((a, b) => {
                const ai = fieldOrder.indexOf(a.field);
                const bi = fieldOrder.indexOf(b.field);
                // columns not in fieldOrder fall back to their server order
                if (ai === -1 && bi === -1) return a.order - b.order;
                if (ai === -1) return 1;
                if (bi === -1) return -1;
                return ai - bi;
            });
        } else {
            headers.sort((a, b) => a.order - b.order);
        }

        for (const header of headers) result.push(createColumn(header, obfuscated));

        return result;
    };

    const prepareFetchParams = ({ resetValues }: { resetValues?: any }) => {
        if (isMounted.current) controller.current?.abort();

        controller.current = new AbortController();
        setLoading(true);

        const isFirstMount = !isMounted.current;

        // Shared with pages that warm a grid's first request (firstGridRequest, ./requestParams):
        // a warmed request is used only when its parameters are exactly these.
        const { apiParams: newApiParams, data: finalData, columnOrder } = gridFetchParams({ isFirstMount, apiParams, sortModel, savedFilters: savedGridFilters(user.settings?.module_settings, tableId), data });
        if (columnOrder) activeColumnOrder.current = columnOrder;

        if (isFirstMount) {
            defaultValues.current = { newApiParams, data: finalData };
            setFiltersState((prev) => ({
                previousState: prev.currentState,
                currentState: "normal",
            }));
        }

        const effectiveApiParams = resetValues ? resetValues.newApiParams : newApiParams;
        const effectiveData = resetValues ? resetValues.data : finalData || {};

        setInitialParam(effectiveApiParams?.initial === true);

        return {
            apiParams: effectiveApiParams,
            data: effectiveData,
            signal: controller.current.signal,
        };
    };

    // Auto-loads without a scrollbar since the last page-1 load; see mayLoadWithoutScroll.
    const unscrolledLoads = useRef(0);
    const warnedUnbounded = useRef(false);

    const fetchData = (page: number = 1, paginationLink?, resetValues?) => {
        // A new first page (sort, filter, data or id change) gets the allowance again.
        if (page === 1) unscrolledLoads.current = 0;
        const { apiParams, data, signal } = prepareFetchParams({ resetValues });

        fetcher({
            page_size: initialRowsQty,
            page,
            filter_options: apiParams,
            universe_id: regionId ? regionId : stock ? stock.stock_id : id,
            data,
            expand_id: expandId,
            signal,
            next_page_url: paginationLink,
        })
            // React 17 renders once per setState outside its own event handlers: the dozen state updates
            // below each re-rendered the whole grid (every cell) in turn, ~1 s of main thread per load on
            // the Index Rebalance tabs. Batched, they render once.
            .then((res) => unstable_batchedUpdates(() => applyResponse(res, page, resetValues)))
            // A failed or superseded load leaves the rows as they were. It used to be an unhandled
            // rejection: a slow endpoint timing out logged an AxiosError on the console.
            .catch(() => undefined)
            .finally(() =>
                unstable_batchedUpdates(() => {
                    isColumnChangeFetch.current = false;
                    setLoading(false);
                    if (defaultValues.current) {
                        setTimeout(() => {
                            setResetFilters(false);
                        }, 200);
                    }
                    if (filtersState.currentState !== "normal") setFiltersState((prev) => ({ previousState: prev.currentState, currentState: "normal" }));
                    setFiltersCleared((prev) => false);
                })
            );
    };

    /** One page of the fetcher's answer into the grid's state (called inside one batch). */
    const applyResponse = (res, page: number, resetValues?) => {
        setFetcherResults &&
            setFetcherResults(
                res?.rows.map((row) => {
                    return { name: row?.name, id: row?.id };
                })
            );

        if (isEmpty(headers) || page === 1) {
            setClickeableCells(res?.columns?.some((header) => !header.onClick));
            if (extraButton) {
                extraButton.forEach((button) => {
                    if (button.columnInfo) {
                        res.columns.push(button.columnInfo);
                    } else if (button.label === "Delete Alert") {
                        res.columns.push({ class: "", field: "delete alert", format: "extra-button", headerName: button.label, on_click: "delete_alert", on_click_argument: "id", order: 6, sortable: false });
                    } else if (button.label === "Edit Alert") {
                        res.columns.push({ class: "", field: "edit alert", format: "extra-button", headerName: button.label, on_click: "edit_alert", on_click_argument: "id", order: 5, sortable: false });
                    } else if (button.label === "Renew Alert" || button.label === "Reset Alert") {
                        return;
                    } else {
                        res.columns.push({ class: "la la-line-chart", field: "show holders", format: "extra-button", headerName: button.label, on_click: "show_holders", on_click_argument: "company_id", order: 6, sortable: false });
                    }
                });
            }

            // Compared by value: every response carries new objects, so comparing references rebuilt the
            // headers on each fetch, which reset every column to its default width and re-flowed the rows
            // until the next autosize (a layout shift, /screening on phones). The column definitions are part
            // of it, so a grid whose columns change (another stock, region or column set) still rebuilds. (The
            // check this replaces also compared header names with column objects, which never matched, so a
            // grid returning availableColumns rebuilt on every response.)
            const source = JSON.stringify([res.columns ?? null, res.columnVisibilityModel ?? null, res.obfuscation ?? null, res.obfuscated ?? null]);
            // A ref, not `headers`: this runs from a fetch started in an earlier render.
            if (headersSource.current !== source) {
                headersSource.current = source;
                // Both of TED's formats (./freeRows): { obfuscation: { obfuscated, columns, doNotObfuscateRows } } or { obfuscated }.
                const obfuscation = readObfuscation(res);
                const isObfuscated = obfuscation.obfuscated;

                const columns = formatHeaders(res.columns, isObfuscated, activeColumnOrder.current);
                setDisableFilters(isObfuscated);
                obfuscatedRef.current = isObfuscated;
                obfuscationRef.current = obfuscation;
                setHeaders(columns);

                setCustomFilters(getFilterableCols(res.columns, res?.columnVisibilityModel));
            }

            setPinnedCols(() => pinnedList.current);

            hiddenColumns.current = { ...res.columnVisibilityModel, ...getExtraHiddenCols(props.hiddenColumns) };
            const visibleColumns = res.columnVisibilityModel && res.columnVisibilityModel.other ? res.columnVisibilityModel.other : res.columnVisibilityModel || {};
            setColumnVisibilityModel({ ...visibleColumns, ...getExtraHiddenCols(props.hiddenColumns) });

            if (res?.availableColumns) {
                // Export available columns list
                if (!isMounted.current) updateAvailableColumns && updateAvailableColumns(res?.availableColumns);

                // Extract current columns from first row.
                currentColumns.current = res.columns.reduce((acc: string[], column: Column) => {
                    if (column.field !== "id") {
                        acc.push(column.field);
                    }
                    return acc;
                }, []);
                // Export current columns list
                if (updateCurrentColumns) {
                    updateCurrentColumns((prevState) => ({
                        ...prevState,
                        columns: currentColumns.current,
                        reset: resetValues && Object.keys(resetValues).length ? true : prevState.reset,
                    }));
                }

                // This may replace line 439.
                setExportingParams && setExportingParams((prevState) => ({ ...prevState, columns: currentColumns.current }));
            }

            totalPages.current = Math.ceil(res.length / initialRowsQty);
            isMounted.current = true;
            if (setExportingParams && !res.availableColumns) {
                const exportColumns = Object.entries(res.columns as Column[]).reduce((acc, column) => {
                    if (column[1].display !== false && column[1].field !== "id") {
                        acc.push(column[1].field);
                    } else if (column[1].field === "ticker") acc.unshift(column[1].field);
                    return acc;
                }, []);
                setExportingParams((prevState) => ({ ...prevState, columns: exportColumns }));
            }
        }

        setPaginationLinks(res.paginationLinks);

        if (setExportingParams) {
            setExportingParams((prevState) => ({ ...prevState, filters: { ...prevState["filters"], sort: res.sortModel } }));
        }
        ///////////////////
        // if (specifiedColumns) {
        //     const notVisibleColumns = res.columns
        //         .filter((column) => !props?.specifiedColumns.includes(column.field))
        //         .reduce((acc, curr) => {
        //             return { ...acc, [curr.field]: false };
        //         }, {});
        //     setColumnVisibilityModel(notVisibleColumns);
        //     setExportingParams((prevState) => ({ ...prevState, columns: specifiedColumns }));
        // }
        ////////////////////
        res.sortModel ? setSortModel(Object.entries(res.sortModel).map(([field, sort]: [string, GridSortDirection]) => ({ field, sort }))) : setSortModel([]);

        if (res.customFilterModel) {
            setCustomFiltersModel(sanitizeFilterItems(res.customFilterModel));
            applyFiltersToChart && applyFiltersToChart({ items: res.customFilterModel.items, page: page });
        } else if (!isColumnChangeFetch.current) {
            setCustomFiltersModel({ items: [] });
        }

        if (res.filterModel) {
            setFilterModel(sanitizeFilterItems(res.filterModel));
        } else if (!isColumnChangeFetch.current) {
            setFilterModel({ items: [] });
        }

        setPinnedRows((prevPinnedRows) => {
            const existingKeys = new Set(prevPinnedRows.top.map((r) => r.indexName));

            const newPinnedRows = res.rows?.filter((r) => r.rowPinning && !existingKeys.has(r.indexName)) ?? [];

            return {
                top: [...prevPinnedRows.top, ...newPinnedRows],
                bottom: [],
            };
        });

        // The rows TED returned unscrambled, by id: only a page-1 answer frees any (an obfuscated grid loads no other page).
        if (page === 1) freeRowsRef.current = freeRowIds(res.rows, readObfuscation(res));

        if (!res.rows?.length && !(page > res.length)) return setRows([]);

        setRows((prevRows) => (page > 1 ? prevRows.concat(res.rows ?? []) : (res.rows ?? [])));

        if (res.paginationLinks) setHasMoreRows(page < totalPages.current);
        else setHasMoreRows(page - 1 + initialRowsQty < res.length);
    };

    /**
     * A grid whose parent gives it no height grows to fit its rows, so
     * scroll-end fires after every page and it fetches the whole result set
     * until the tab dies (the /earnings freeze of 2026-09-27). A page that
     * does not fill a tall window still needs the next one or two, so a few
     * loads without a scrollbar are allowed before the grid stops asking.
     */
    const mayLoadWithoutScroll = () => {
        if (autoHeight) return true;
        const scroller = tableRef.current?.rootElementRef?.current?.querySelector<HTMLElement>(".MuiDataGrid-virtualScroller");
        if (!scroller || scroller.scrollHeight > scroller.clientHeight) {
            unscrolledLoads.current = 0;
            return true;
        }
        if (unscrolledLoads.current < 2) {
            unscrolledLoads.current += 1;
            return true;
        }
        if (process.env.NODE_ENV !== "production" && !warnedUnbounded.current) {
            warnedUnbounded.current = true;
            console.warn(`DataGrid ${tableId ?? ""}: the grid never scrolls, so it is not loading more pages; give its parent a bounded height.`);
        }
        return false;
    };

    const loadMoreData = () => {
        if (onePage || loading || obfuscatedRef.current) return;
        if (!isMounted.current || !paginationLinks?.next) return;
        if (!mayLoadWithoutScroll()) return;

        const next = paginationLinks.next.split("page=")[1].split("&")[0];
        const url = new URL(paginationLinks.next);
        // url.searchParams.set("page_size", perPageRowsQty);
        url.searchParams.delete("authid");
        url.searchParams.delete("GUIv");
        fetchData(next, url.toString());
    };

    const onRowClick = (params?: { id?: string | number }) => {
        // A free row is read like a member's (its stock cell links to the stock page); the rest open the upgrade.
        if (obfuscatedRef.current && !(params && freeRowsRef.current.has(params.id))) {
            trackUpgradeClick(UPGRADE_LOCATIONS.DATA_GRID_CELL);
            openUpgrade();
        }
    };

    useEffect(() => {
        if (setFilters && customFilters.length > 0) {
            setFilters((prevState) => {
                return (
                    <>
                        {/* if the prevState children isnt an array, then is the initial one defined on the parent component */}
                        {!Array.isArray(prevState?.props?.children) ? prevState : prevState.props.children[0]}
                        <HeaderFilters
                            filters={currentColumns.current.length ? customFilters.filter((filt) => currentColumns.current.includes(filt.field)) : customFilters}
                            setParams={setApiParams}
                            setCustomFiltersModel={setCustomFiltersModel}
                            values={resetFilters ? [...filterModel.items] : [...customFilterModel.items, ...filterModel.items]}
                            fetcher={fetchData}
                            defaultValues={defaultValues.current}
                            setResetFilters={setResetFilters}
                            resetFilters={resetFilters}
                            data={data}
                            reset={reset}
                            chips={chips}
                            setChips={setChips}
                            isNewsSearch={isNewsSearch}
                            stockColumns={filterColumnType || instrumentColumns(headers)}
                            initialParam={initialParam}
                            tableId={tableId}
                            maxFilterSets={maxFilterSets}
                            filtersCleared={filtersCleared}
                            setFiltersCleared={setFiltersCleared}
                            filtersState={filtersState}
                            setFiltersState={setFiltersState}
                            noCalendarPresets={noCalendarPresets}
                            forwardLookingDateFields={forwardLookingDateFields}
                        />
                    </>
                );
            });
        }

        if (setExportingParams && customFilterModel.items.length > 0) {
            const formattedSortModel = sortModel.reduce((result, { field, sort }) => {
                result[field] = sort;
                return result;
            }, {});
            setExportingParams((prevState) => ({ ...prevState, customFilterModel, sortModel: formattedSortModel }));
        }
    }, [setFilters, customFilters, customFilterModel, filterModel, specifiedColumns, resetFilters, chips]);

    useEffect(() => {
        if (!resetFilters) {
            if (isMounted.current) isColumnChangeFetch.current = true;
            fetchData();
        }
    }, [data, id, stock, specifiedColumns, regionId]);

    useEffect(() => {
        if (!isMounted.current) return;
        else if (!resetFilters) {
            tableRef?.current?.scrollToIndexes({ rowIndex: 0, colIndex: 0 });
            page.current = 1;
            fetchData();
        }
    }, [apiParams]);

    const onSortModelChange = (newSortModel: GridSortModel) => {
        // A locked grid never sorts, free rows or not (its columns are not sortable; this also covers any other way in).
        if (obfuscatedRef.current) return;
        // MUI X dropping a sort it cannot apply while it rebuilds its columns is not the reader sorting: refetching
        // for it requested and drew the same first page twice (see ./sortModel).
        if (isColumnCleanupOnly(sortModel, newSortModel, (field) => !!tableRef.current?.getColumn(field))) return;
        const hasSortModelChanged = newSortModel?.length !== sortModel?.length || newSortModel.some((item) => !sortModel.includes(item));

        if (hasSortModelChanged) {
            const newModel = newSortModel.reduce((result, { field, sort }) => {
                result[field] = sort;
                return result;
            }, {});

            setSortModel(newSortModel);
            setApiParams({ ...apiParams, sortModel: newModel, customFilterModel, filterModel });
        }
    };

    const onFilterModelChange = (newFilterModel: GridFilterModel) => {
        !areEqual(newFilterModel, filterModel) && setFilterModel(newFilterModel);
    };

    const updateRowsHeight = () => tableRef.current.resetRowHeights();

    const debouncedUpdateRowsHeight = () => {
        clearTimeout(rowsHeightUpdateTimeout);
        rowsHeightUpdateTimeout = setTimeout(updateRowsHeight, 300);
    };

    // Autosize only the fixed-width columns. On desktop the columns are `flex` and share the grid's
    // width; MUI X 7+ would autosize those too (turning flex off), which MUI X 6 did not.
    const autosizeColumns = () => {
        const api = tableRef.current;
        if (!api) return Promise.resolve();
        const columns = api
            .getAllColumns()
            .filter((col) => !col.flex)
            .map((col) => col.field);
        return columns.length ? api.autosizeColumns({ ...autosizeOptions, columns }) : Promise.resolve();
    };

    const handleWindowResize = () => {
        windowWidth.current = window.innerWidth;
        debouncedUpdateRowsHeight();

        autosizeColumns();
    };

    useEffect(() => {
        window.addEventListener("resize", handleWindowResize);

        return () => {
            window.removeEventListener("resize", handleWindowResize);
            setClickeableCells(null);
            clearTimeout(rowsHeightUpdateTimeout);
            if (columnSaveTimeout.current) clearTimeout(columnSaveTimeout.current);
        };
    }, []);

    // The first rows paint at the default height, then the columns autosize, the text wraps and every
    // row grows: a layout shift of the whole list on each visit (phones, and desktop lists whose rows
    // are of "auto" height). The first page is laid out but kept invisible until that first autosize
    // and row measurement are done (at most 1.5 s), so the rows appear once, at their final height.
    const [rowsSettled, setRowsSettled] = useState(false);
    const settleStarted = useRef(false);
    const settleTimers = useRef<ReturnType<typeof setTimeout>[]>([]);
    const settleFrames = useRef<number[]>([]);
    // Whatever happens to the data, the rows never stay hidden: at most 2.5 s after the first rows
    // arrive. Counted from the rows, not from mount, or a page whose data took longer than that (slow
    // phones, cold caches) was revealed empty and its rows then grew in view.
    const hasRows = rows.length > 0;

    // An obfuscated answer with free rows (./freeRows): the unlock banner covers the scrambled rows only,
    // from the first one on screen down, and follows them as the reader scrolls the loaded rows. Null while
    // no scrambled row is on screen (or the visible strip is too short for the banner): no banner over free
    // rows. Undefined when the answer frees no row: the banner sits where it always has.
    const [lockedTop, setLockedTop] = useState<number | null | undefined>(undefined);
    const hasFreeRows = obfuscatedRef.current && freeRowsRef.current.size > 0;
    const allRowsFree = hasFreeRows && firstScrambledRowId(rows, freeRowsRef.current) === null;
    useEffect(() => {
        if (!hasFreeRows || allRowsFree) {
            setLockedTop(undefined);
            return;
        }
        const root = tableRef.current?.rootElementRef?.current;
        const scroller = root?.querySelector<HTMLElement>(".MuiDataGrid-virtualScroller");
        if (!root || !scroller) return;
        let frame = 0;
        const measure = () => {
            frame = 0;
            const holder = root.offsetParent as HTMLElement | null;
            const header = root.querySelector<HTMLElement>(".MuiDataGrid-columnHeaders");
            if (!holder) return setLockedTop(null);
            const viewport = { top: header ? header.getBoundingClientRect().bottom : scroller.getBoundingClientRect().top, bottom: scroller.getBoundingClientRect().bottom };
            const free = new Set([...freeRowsRef.current].map(String));
            const rendered: RenderedRow[] = Array.from(root.querySelectorAll<HTMLElement>(".MuiDataGrid-row[data-id]")).map((el) => {
                const rect = el.getBoundingClientRect();
                return { id: el.dataset.id ?? "", top: rect.top, bottom: rect.bottom };
            });
            const top = lockedRegionTop(rendered, free, viewport);
            if (top === null || viewport.bottom - top < LOCKED_BANNER_MIN_HEIGHT) return setLockedTop(null);
            const holderRect = holder.getBoundingClientRect();
            setLockedTop(Math.round(top - holderRect.top - holder.clientTop + holder.scrollTop));
        };
        const schedule = () => {
            if (!frame) frame = requestAnimationFrame(measure);
        };
        schedule();
        scroller.addEventListener("scroll", schedule, { passive: true });
        window.addEventListener("resize", schedule);
        const observer = new ResizeObserver(schedule);
        observer.observe(root);
        return () => {
            if (frame) cancelAnimationFrame(frame);
            scroller.removeEventListener("scroll", schedule);
            window.removeEventListener("resize", schedule);
            observer.disconnect();
        };
    }, [rows, hasFreeRows, allRowsFree, rowsSettled]);
    useEffect(() => {
        if (hasRows && !rowsSettled) settleTimers.current.push(setTimeout(() => setRowsSettled(true), 2500));
    }, [hasRows]);
    useEffect(
        () => () => {
            settleTimers.current.forEach(clearTimeout);
            settleFrames.current.forEach(cancelAnimationFrame);
        },
        []
    );

    useEffect(() => {
        if (tableRef.current && rows.length <= initialRowsQty) {
            // Timers and frames are kept so the unmount cleanup above can cancel them.
            const later = (fn: () => void, ms: number) => settleTimers.current.push(setTimeout(fn, ms));
            const frame = (fn: () => void) => settleFrames.current.push(requestAnimationFrame(fn));
            const settle = rows.length > 0 && !settleStarted.current;
            if (settle) settleStarted.current = true;
            const reveal = () => setRowsSettled(true);
            if (settle) later(reveal, 1500);
            // Two frames first: autosizing in the render that receives the rows measured cells not yet
            // drawn, left the columns at their default width, and the next row update sized them in
            // view (MUI X 9, /screening on phones).
            frame(() =>
                frame(() => {
                    const sized = autosizeColumns();
                    if (!settle) return;
                    Promise.resolve(sized)
                        .then(() => {
                            tableRef.current?.resetRowHeights();
                            // Two frames: the grid re-renders with the new widths, then measures the rows.
                            frame(() => frame(() => later(reveal, 50)));
                        })
                        .catch(reveal);
                })
            );
        }
    }, [rows]);

    useEffect(() => {
        if (extraRow && rows.length) {
            const ids = [...extraRow.map((row) => row.id).filter((rowId) => rowId !== undefined), ...extraRowIds.current];
            const dropped = new Set(ids.slice(MAX_EXTRA_ROWS));
            extraRowIds.current = ids.slice(0, MAX_EXTRA_ROWS);
            setRows((prevRows) => [...extraRow, ...(dropped.size ? prevRows.filter((row) => !dropped.has(row.id)) : prevRows)]);
        }
    }, [extraRow]);

    useEffect(() => {
        return () => {
            if (button !== undefined && button.tsFlags) {
                button.series = [];
            }
        };
    }, [id]);

    const TooltipRowComponent = useMemo(() => {
        if (!rowTooltipField) return undefined;
        const bgColor = backgroundColor;
        const fgColor = textColor;
        const field = rowTooltipField;
        return function TooltipRow(rowProps: any) {
            const tooltipContent = rowProps.row?.[field];
            if (!tooltipContent) return <GridRow {...rowProps} />;
            return (
                <HtmlTooltip
                    title={
                        <span style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                            <svg viewBox="0 0 512 512" width="16" height="16" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0, marginTop: 2 }}>
                                <path fill="#ED2F2F" d="M256 32l224 400H32L256 32z" stroke="#ED2F2F" strokeWidth="32" strokeLinejoin="round" />
                                <path fill="#FFFFFF" d="M256 148c13.255 0 24 10.745 24 24v120c0 13.255-10.745 24-24 24s-24-10.745-24-24V172c0-13.255 10.745-24 24-24zm0 216c13.255 0 24 10.745 24 24s-10.745 24-24 24-24-10.745-24-24 10.745-24 24-24z" />
                            </svg>
                            <span dangerouslySetInnerHTML={{ __html: tooltipContent }} />
                        </span>
                    }
                    followCursor
                    arrow
                    bgColor={bgColor}
                    fgColor={fgColor}
                    slotProps={{
                        popper: { modifiers: [{ name: "offset", options: { offset: [0, 8] } }] },
                    }}
                >
                    <GridRow {...rowProps} />
                </HtmlTooltip>
            );
        };
    }, [rowTooltipField, backgroundColor, textColor]);

    // Master-detail (`detailPanel`): MUI's panel under a row, its toggle the first, pinned column.
    const gridColumns = useMemo(
        () =>
            detailPanel
                ? [
                      {
                          ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
                          width: 40,
                          minWidth: 40,
                          renderCell: (params: GridRenderCellParams) => <DetailPanelToggle {...params} label={detailPanel.label ?? "details"} />,
                      },
                      ...headers,
                  ]
                : headers,
        [headers, detailPanel]
    );
    const gridPinnedCols = useMemo(() => (detailPanel ? { ...pinnedCols, left: [GRID_DETAIL_PANEL_TOGGLE_FIELD, ...(pinnedCols.left ?? []).filter((field) => field !== GRID_DETAIL_PANEL_TOGGLE_FIELD)] } : pinnedCols), [pinnedCols, detailPanel]);
    const getDetailPanelContent = useCallback(({ row }) => (detailPanel ? detailPanel.content(row) : null), [detailPanel]);
    const getDetailPanelHeight = useCallback(() => "auto" as const, []);

    return (
        <>
            <DataGridStyles />

            <TableCellContext.Provider value={cellShared}>
                <DataGridPremium
                    {...PREMIUM_FEATURES_OFF}
                    disableRowSelectionOnClick
                    disableColumnFilter
                    hideFooter
                    getRowClassName={getRowClassName}
                    apiRef={tableRef}
                    columns={gridColumns}
                    columnVisibilityModel={columnVisibilityModel}
                    slots={{
                        noRowsOverlay: () => <NoRowsComponent noRowsMessage={noRowsMessage} />,
                        columnMenuIcon: () => <MoreVertIcon style={{ color: theme === "dark" ? "#fff" : undefined }} />,
                        ...(TooltipRowComponent && { row: TooltipRowComponent }),
                    }}
                    disableColumnSelector={true}
                    getRowHeight={() => customRowHeight}
                    filterMode="server"
                    filterModel={sanitizeFilterItems({ items: [...filterModel.items, ...customFilterModel.items] })}
                    loading={loading}
                    onFilterModelChange={onFilterModelChange}
                    onRowClick={!clickeableCells ? onRowClick : null}
                    onColumnOrderChange={() => {
                        if (!tableRef.current) return;
                        const orderedFields = tableRef.current
                            .getAllColumns()
                            .filter((col) => col.field !== "id" && col.field !== "__check__" && col.field !== GRID_DETAIL_PANEL_TOGGLE_FIELD)
                            .map((col) => col.field);
                        currentColumns.current = orderedFields;
                        activeColumnOrder.current = orderedFields;
                        if (updateCurrentColumns) {
                            updateCurrentColumns((prevState) => ({ ...prevState, columns: orderedFields }));
                        }
                        if (columnSaveTimeout.current) clearTimeout(columnSaveTimeout.current);
                        columnSaveTimeout.current = setTimeout(() => saveColumnOrderRef.current?.(orderedFields), 2000);
                    }}
                    sortingMode="server"
                    onSortModelChange={onSortModelChange}
                    initialState={{
                        sorting: { sortModel: sortModel },
                    }}
                    sortModel={sortModel}
                    rows={rows}
                    pinnedRows={extraPinnedTopRows?.length ? { top: [...extraPinnedTopRows, ...pinnedRows.top], bottom: pinnedRows.bottom } : pinnedRows}
                    density="standard"
                    // MUI X 8+ draws a scroll shadow over the grid, positioned from the pinned columns'
                    // width: it moved each time they autosized (a layout shift on phone lists). The
                    // border alone is how the pinned edge looked before.
                    pinnedColumnsSectionSeparator="border"
                    pinnedRowsSectionSeparator="border"
                    autoHeight={autoHeight}
                    // With rows of "auto" height the grid sizes itself from estimated heights unless
                    // every row is rendered and measured; autoHeight lists are short, so render them all.
                    disableVirtualization={autoHeight}
                    sx={{
                        ...tableStyleOptions,
                        ...(props.onRowClick && cellHoverStyleOptions),
                        ...useCellStyleOptions(),
                        // Locked: no scrolling, except through the loaded rows when some are free to read (the
                        // next page still never loads: loadMoreData stops on obfuscatedRef).
                        ...(obfuscatedRef.current &&
                            !hasFreeRows && {
                                "& .MuiDataGrid-virtualScroller": {
                                    overflow: "hidden !important",
                                },
                            }),
                        ...(!rowsSettled && {
                            "& .MuiDataGrid-virtualScrollerContent": { visibility: "hidden" },
                        }),
                    }}
                    onRowsScrollEnd={loadMoreData}
                    pinnedColumns={gridPinnedCols}
                    onPinnedColumnsChange={handlePinnedColsChange}
                    {...(detailPanel && { getDetailPanelContent, getDetailPanelHeight })}
                    {...(isMobile ? { onColumnResize: debouncedUpdateRowsHeight } : { onColumnWidthChange: updateRowsHeight })}
                    autosizeOnMount={isMobile || isOrtexApp()}
                    autosizeOptions={autosizeOptions}
                    disableColumnMenu={obfuscatedRef.current}
                />
            </TableCellContext.Provider>

            {isMounted.current && obfuscatedRef.current && rows.length && !props.hideObfuscationBanner && (!hasFreeRows || typeof lockedTop === "number") ? <ObfuscationBanner theme={theme} top={hasFreeRows ? lockedTop : undefined} /> : null}
            <SnackBar open={snackBar.open} setOpen={(open) => setSnackBar((prev) => ({ ...prev, open }))} severity={snackBar.severity} text={snackBar.text} />
        </>
    );
};

export const MemoizedDataGrid = memo(DataGrid, areEqual);
export const SimpleMemoizedDataGrid = memo(DataGrid);
